import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { BitfFile } from '@bitf/core/models/bitf-file.model';
import { AiPromptPreset, Archive, Image } from '@common/core/models';
import { StoreService } from '@common/core/services';
import { AiGenerationService, AiGeneratedImage } from '@common/core/services/api/ai-generation.service';
import { AiPromptPresetService } from '@common/core/services/graph-ql/ai-prompt-preset.service';
import { ArchivesService } from '@common/core/services/graph-ql/archives.service';
import { FilesService } from '@common/core/services/graph-ql/files.service';
import { ToastMessagesService } from '@services';
import { EBitfUiMessageType } from '@common/enums';
import { IArchiveImageGallery, IBitfGraphQlResponse } from '@interfaces';
import { ImagesSelection, SelectedImage } from '../file-uploader-tiles/file-uploader-tiles.component';
import { eStoreActions } from '@web/enums';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mpa-ai-image-generation',
  styleUrl: './ai-image-generation.component.scss',
  templateUrl: './ai-image-generation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiImageGenerationComponent implements OnInit, OnDestroy {
  // List of presets from Strapi
  promptPresets: AiPromptPreset[] = [];
  readonly originalAspectRatio = null;
  // Available aspect ratios for the AI generation
  aspectRatios: string[] = [
    this.originalAspectRatio,
    '1:1',
    '2:3',
    '3:4',
    '4:5',
    '9:16',
    '16:9',
    '5:4',
    '4:3',
    '3:2',
  ];
  // Selected preset for AI generation
  // List of images from presets
  selectedPreset: AiPromptPreset = new AiPromptPreset({
    label: '',
    prompt: '',
    medias: [],
    aspect_ratio: this.originalAspectRatio,
  });
  selectedAspectRatio = this.originalAspectRatio;
  // Output image resolution requested to the AI generation (imageConfig.imageSize)
  imageSizes: string[] = ['1K', '2K', '4K'];
  selectedImageSize = '1K';
  // List of personal uploads
  personalGenerationImages: SelectedImage[] = [];
  // Selected images from the item gallery, in selection order: they are sent to the
  // AI generation after the preset medias and personal uploads, so their image_N
  // numbering continues from the uploads count
  selectedImages: Image[] = [];
  // Image generated by the AI
  generatedImage: { response: AiGeneratedImage; url: SafeUrl };

  // State
  isLoading = false;
  subscriptions = new Subscription();

  // Before/after comparison between the selected image and the generated one
  compareView: null | 'before' | 'slider' | 'after' = null;
  sliderPosition = 50;
  isDragging = false;

  get selectedItem(): Archive {
    return this.storeService.store.selectedItem;
  }

  // Step 1 grid images: item images first, then suit items, links and lookbooks
  // (allGalleryImages on the model); the image_N numbering follows the selection order
  get imagesList(): IArchiveImageGallery[] {
    return this.selectedItem?.allGalleryImages || [];
  }

  trackByImageId(index: number, image: Image): number {
    return image.id;
  }

  compareById(o1: AiPromptPreset, o2: AiPromptPreset): boolean {
    return o1?.id === o2?.id;
  }

  constructor(
    public storeService: StoreService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private toastMessagesService: ToastMessagesService,
    private aiPromptPresetService: AiPromptPresetService,
    private aiGenerationService: AiGenerationService,
    private filesService: FilesService,
    private archiveService: ArchivesService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.aiPromptPresetService.get({ page: 0, size: 100000 }).subscribe(response => {
        this.promptPresets = response.content || [];
      })
    );

    // Reset the selected image and generated image when changing the selected item
    this.subscriptions.add(
      this.storeService.selectStore([eStoreActions.SET_SELECTED_ITEM]).subscribe(() => {
        this.selectedImages = [];
        this.generatedImage = null;
      })
    );
  }

  setCustomPreset() {
    // Replace the instance (instead of mutating it) so the mat-select [value] binding re-binds
    // and switches to the CUSTOM_PRESET option
    const customPreset = new AiPromptPreset(null, this.selectedPreset);
    customPreset.id = null;
    customPreset.medias = [...(this.selectedPreset.medias || [])];
    this.selectedPreset = customPreset;
  }

  onImagesChanged(selection: ImagesSelection) {
    this.personalGenerationImages = selection.files;
    // Any change to the images invalidates the selected preset
    this.setCustomPreset();
  }

  onSelectImage(image: Image) {
    const index = this.selectedImages.indexOf(image);
    if (index === -1) {
      this.selectedImages = [...this.selectedImages, image];
    } else {
      this.selectedImages = this.selectedImages.filter(selected => selected !== image);
    }
    this.resetCompare();
  }

  isSelectedImage(image: Image): boolean {
    return this.getSelectedImageNumber(image) !== null;
  }

  getSelectedImageNumber(image: Image): number | null {
    const index = this.selectedImages.indexOf(image);
    if (index === -1) {
      return null;
    }
    // The numbering continues after the preset medias and personal uploads (sent first)
    const uploadsCount = (this.selectedPreset?.medias?.length || 0) + this.personalGenerationImages.length;
    return uploadsCount + index + 1;
  }

  get compareImage(): Image {
    return this.selectedImages[0];
  }

  setCompareView(view: null | 'before' | 'slider' | 'after') {
    this.compareView = view;
  }

  onComparePointerDown(event: PointerEvent) {
    event.preventDefault();
    this.isDragging = true;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.updateSliderPosition(event);
  }

  onComparePointerMove(event: PointerEvent) {
    if (!this.isDragging) {
      return;
    }
    this.updateSliderPosition(event);
  }

  onComparePointerUp(event: PointerEvent) {
    this.isDragging = false;
  }

  onCompareKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowLeft':
        this.sliderPosition = this.clampSliderPosition(this.sliderPosition - 2);
        break;
      case 'ArrowRight':
        this.sliderPosition = this.clampSliderPosition(this.sliderPosition + 2);
        break;
      case 'Home':
        this.sliderPosition = 0;
        break;
      case 'End':
        this.sliderPosition = 100;
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  onSelectPreset(preset: AiPromptPreset) {
    if (!preset?.id) {
      // Custom option explicitly chosen: reset to a blank custom state
      const blankPreset = new AiPromptPreset({ label: '', prompt: '', medias: [] });
      blankPreset.id = null;
      this.selectedPreset = blankPreset;
      return;
    }
    // Clone the preset and copy the medias array so the source preset from Strapi is never mutated
    const clone = new AiPromptPreset(null, preset);
    clone.medias = [...(preset.medias || [])];
    this.selectedPreset = clone;
    this.selectedAspectRatio = clone.aspect_ratio;
  }

  onResetAll() {
    this.selectedPreset = new AiPromptPreset({ label: '', prompt: '', medias: [] });
    this.selectedPreset.id = null;
    this.selectedAspectRatio = this.originalAspectRatio;
    this.selectedImageSize = '1K';
    this.selectedImages = [];
    this.generatedImage = null;
    this.resetCompare();
  }

  onGenerate() {
    if (this.isLoading || !this.selectedPreset?.prompt?.trim()) {
      return;
    }

    const formData = new FormData();
    formData.set('prompt', this.selectedPreset?.prompt);
    if (this.selectedAspectRatio !== this.originalAspectRatio) {
      formData.set('aspect_ratio', this.selectedAspectRatio);
    }
    formData.set('image_size', this.selectedImageSize);
    // NOTE: the images order matters for the AI generation: preset medias and personal
    // uploads first, then the images selected from the item gallery
    this.generationImages.forEach((selectedImage, index) => {
      const imageIndex = index + 1;
      if (selectedImage instanceof Image) {
        const url = selectedImage.getImageByFormat('xlarge')?.url;
        if (url) {
          formData.set(`image_${imageIndex}`, url);
        }
      } else {
        formData.set(`image_${imageIndex}`, selectedImage.fileObject);
      }
    });

    this.isLoading = true;
    this.aiGenerationService
      .generateImage(formData)
      .pipe(
        tap(response => {
          this.generatedImage = {
            response,
            url: this.sanitizer.bypassSecurityTrustResourceUrl(
              `data:${response.mimeType};base64,${response.base64Data}`
            ),
          };
          this.resetCompare();
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        error: () => {
          this.toastMessagesService.show({
            title: this.translateService.instant('AI_IMAGE_GENERATION.GENERATION_ERROR'),
            type: EBitfUiMessageType.ERROR,
          });
        },
      });
  }

  onDownload() {
    if (!this.generatedImage) {
      return;
    }
    const blob = this.getGeneratedImageBlob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.getGeneratedImageFileName();
    anchor.click();
    URL.revokeObjectURL(url);
  }

  onSaveIntoItem() {
    if (!this.generatedImage || !this.selectedItem) {
      return;
    }

    const file = new File([this.getGeneratedImageBlob()], this.getGeneratedImageFileName(), {
      type: this.generatedImage.response.mimeType,
    });

    this.isLoading = true;
    this.filesService
      .createImage({
        variables: { file: new BitfFile(file) },
        context: { useMultipart: true },
        disableHideLoader: true,
      })
      .pipe(
        switchMap((response: IBitfGraphQlResponse<Image>) => {
          const updateArchive = new Archive(undefined, {
            id: this.selectedItem.id,
            images: this.selectedItem.images,
          });
          updateArchive.images.push(response.content);
          return this.archiveService.updateItem({ disableHideLoader: true, body: updateArchive });
        }),
        tap((response: IBitfGraphQlResponse<Archive>) => {
          this.storeService.updateSelectedItem(response.content);
          this.toastMessagesService.show({
            title: this.translateService.instant('AI_IMAGE_GENERATION.SAVE_SUCCESS'),
            type: EBitfUiMessageType.SUCCESS,
          });
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  private resetCompare() {
    this.compareView = null;
    this.sliderPosition = 50;
    this.isDragging = false;
  }

  private clampSliderPosition(value: number): number {
    return Math.max(0, Math.min(100, value));
  }

  private updateSliderPosition(event: PointerEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    if (!rect.width) {
      return;
    }
    this.sliderPosition = this.clampSliderPosition(((event.clientX - rect.left) / rect.width) * 100);
  }

  private getGeneratedImageBlob(): Blob {
    const { base64Data, mimeType } = this.generatedImage.response;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }

  private getGeneratedImageFileName(): string {
    return `ai-generation-${Date.now()}.${this.generatedImage.response.fileExtension || 'png'}`;
  }

  // NOTE: ordered images sent to the AI generation: the preset medias and personal
  // uploads first (image_1, ...), then the item gallery selected images in selection order
  private get generationImages(): SelectedImage[] {
    return [
      ...(this.selectedPreset?.medias || []),
      ...this.personalGenerationImages,
      ...(this.selectedImages || []),
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
